import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  CreditCard, Shield, Check, X, Loader2, AlertTriangle,
  Key, Eye, EyeOff, TestTube, Trash2
} from 'lucide-react';

// Les libellés/descriptions sont traduits à l'affichage via t(descKey)/t(labelKey) —
// PROVIDERS est défini au niveau module où le hook n'est pas disponible.
const PROVIDERS = [
  { id: 'stripe', name: 'Stripe', descKey: 'paymentConfig.stripeDesc', color: '#635BFF',
    fields: [
      { key: 'public_key', labelKey: 'paymentConfig.fieldPublicKey', required: true },
      { key: 'secret_key', labelKey: 'paymentConfig.fieldSecretKey', required: true, secret: true },
      { key: 'webhook_secret', labelKey: 'paymentConfig.fieldWebhookSecret', secret: true }
    ]
  },
  { id: 'sumup', name: 'SumUp', descKey: 'paymentConfig.sumupDesc', color: '#1A4CFF',
    fields: [
      { key: 'api_key', labelKey: 'paymentConfig.fieldApiKey', secret: true },
      { key: 'merchant_code', labelKey: 'paymentConfig.fieldMerchantCode', required: true },
      { key: 'client_id', labelKey: 'paymentConfig.fieldClientId' },
      { key: 'client_secret', labelKey: 'paymentConfig.fieldClientSecret', secret: true }
    ]
  }
];

const PaymentConfigPage = () => {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [keys, setKeys] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState('');

  const loadConfig = useCallback(async () => {
    try {
      const res = await api.get('/payment-config');
      setConfig(res.data);
      if (res.data.provider) setSelectedProvider(res.data.provider);
      if (res.data.keys) {
        const k = {};
        for (const [field, val] of Object.entries(res.data.keys)) {
          if (field === 'merchant_code' || field === 'client_id') {
            k[field] = val || '';
          } else {
            k[field] = '';
          }
        }
        setKeys(k);
      }
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleSave = async () => {
    setError(''); setSaving(true); setTestResult(null);
    try {
      const payload = { provider: selectedProvider, keys: { ...keys }, test_mode: false };
      // Ne pas renvoyer les secrets masqués (sup_sk_…xxxx) — le backend garde l'existant
      for (const [k, v] of Object.entries(payload.keys)) {
        if (typeof v === 'string' && (v.includes('...') || v === '****')) {
          payload.keys[k] = '';
        }
      }
      await api.post('/payment-config', payload);
      await loadConfig();
    } catch (err) {
      setError(err.response?.data?.detail || t('paymentConfig.saveError'));
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const res = await api.post('/payment-config/test');
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ status: 'error', message: err.response?.data?.detail || t('paymentConfig.testError') });
    } finally { setTesting(false); }
  };

  const handleDisable = async () => {
    if (!window.confirm(t('paymentConfig.disableConfirm'))) return;
    try {
      await api.delete('/payment-config');
      setSelectedProvider(null); setKeys({}); setTestResult(null);
      await loadConfig();
    } catch { /* ignore */ }
  };

  if (loading) return <Layout><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div></Layout>;

  const provider = PROVIDERS.find(p => p.id === selectedProvider);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto fade-in" data-testid="payment-config-page">
        <div className="flex items-center gap-3 mb-6">
          <CreditCard className="w-5 h-5 text-[#009b3a]" />
          <h1 className="text-xl font-bold text-white">{t('paymentConfig.title')}</h1>
        </div>

        {/* Status Banner */}
        {config?.configured ? (
          <div className="mb-6 p-4 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center gap-3" data-testid="payment-status-active">
            <Check className="w-5 h-5 text-green-400" />
            <div>
              <p className="text-sm font-semibold text-green-300">{t('paymentConfig.statusActiveTitle')}</p>
              <p className="text-xs text-green-400/60">{t('paymentConfig.providerLabel', { name: PROVIDERS.find(p => p.id === config.provider)?.name || config.provider })}</p>
            </div>
            <button onClick={handleDisable} className="ml-auto text-red-400 hover:text-red-300 p-2" data-testid="disable-payments-btn"><Trash2 className="w-4 h-4" /></button>
          </div>
        ) : (
          <div className="mb-6 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20 flex items-center gap-3" data-testid="payment-status-inactive">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            <div>
              <p className="text-sm font-semibold text-yellow-300">{t('paymentConfig.statusInactiveTitle')}</p>
              <p className="text-xs text-yellow-400/60">{t('paymentConfig.statusInactiveDesc')}</p>
            </div>
          </div>
        )}

        {/* Provider Selection */}
        <div className="mb-6">
          <p className="text-sm text-gray-400 mb-3">{t('paymentConfig.chooseProvider')}</p>
          <div className="grid grid-cols-3 gap-3">
            {PROVIDERS.map(p => (
              <button key={p.id} onClick={() => { setSelectedProvider(p.id); setKeys({}); setTestResult(null); }}
                data-testid={`provider-${p.id}`}
                className={`p-4 rounded-xl border text-left transition-all ${selectedProvider === p.id ? 'border-[#009b3a] bg-[#009b3a]/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}>
                <div className="text-sm font-bold text-white mb-1">{p.name}</div>
                <div className="text-[10px] text-gray-500">{t(p.descKey)}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Key Configuration */}
        {provider && (
          <div className="space-y-4 mb-6" data-testid="payment-keys-form">
            <div className="flex items-center gap-2 mb-2">
              <Key className="w-4 h-4 text-gray-400" />
              <h3 className="text-sm font-semibold text-white">{t('paymentConfig.apiKeysTitle', { name: provider.name })}</h3>
            </div>
            <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-3">
              {provider.fields.map(f => (
                <div key={f.key}>
                  <label className="block text-xs text-gray-400 mb-1">{t(f.labelKey)} {f.required && <span className="text-red-400">*</span>}</label>
                  <div className="relative">
                    <input
                      type={f.secret && !showSecrets[f.key] ? 'password' : 'text'}
                      value={keys[f.key] || ''}
                      onChange={e => setKeys({ ...keys, [f.key]: e.target.value })}
                      data-testid={`key-${f.key}`}
                      className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white font-mono focus:outline-none focus:border-[#009b3a]/40 pr-10"
                      placeholder={
                        f.secret && config?.keys_set?.[f.key]
                          ? t('paymentConfig.savedPlaceholder', { value: config.keys[f.key] || '****' })
                          : t(f.labelKey)
                      }
                    />
                    {f.secret && (
                      <button type="button" onClick={() => setShowSecrets(s => ({ ...s, [f.key]: !s[f.key] }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                        {showSecrets[f.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {error && <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400">{error}</div>}

            {testResult && (
              <div className={`p-3 rounded-lg text-xs ${testResult.status === 'ok' ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`} data-testid="test-result">
                {testResult.status === 'ok' ? <Check className="w-3.5 h-3.5 inline mr-1" /> : <X className="w-3.5 h-3.5 inline mr-1" />}
                {testResult.message}
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={handleSave} disabled={saving} data-testid="save-payment-config"
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm text-[#050a14] bg-gradient-to-r from-[#009b3a] to-[#fedd00] hover:opacity-90 disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />} {t('paymentConfig.save')}
              </button>
              {config?.configured && (
                <button onClick={handleTest} disabled={testing} data-testid="test-payment-config"
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm text-white border border-white/10 hover:bg-white/5 disabled:opacity-50">
                  {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />} {t('paymentConfig.testConnection')}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Info */}
        <div className="p-4 rounded-xl bg-white/5 border border-white/10">
          <h3 className="text-xs font-semibold text-gray-400 mb-2">{t('paymentConfig.infoTitle')}</h3>
          <ul className="text-xs text-gray-500 space-y-1.5">
            <li>{t('paymentConfig.info1')}</li>
            <li>{t('paymentConfig.info2')}</li>
            <li>{t('paymentConfig.info3')}</li>
            <li>{t('paymentConfig.info4')}</li>
          </ul>
        </div>
      </div>
    </Layout>
  );
};

export default PaymentConfigPage;
